import EventEmitter from 'eventemitter3'

type RawOperation = [number, OP, string, string, number]

// https://www.sqlite.org/capi3ref.html
export const OP = {
  SQLITE_DELETE: 9,
  SQLITE_INSERT: 18,
  SQLITE_UPDATE: 23
} as const

type OP = (typeof OP)[keyof typeof OP]

export type Operation = {
  p: number
  op: OP
  dbName: string
  tbl: string
  rowid: number
}

export type UpdateCondition = {
  tbl: string
  op: typeof OP.SQLITE_UPDATE
  ids?: Array<string | number>
}

export type DeleteCondition = {
  tbl: string
  op: typeof OP.SQLITE_DELETE
  ids?: Array<string | number>
}

export type InsertCondition = {
  tbl: string
  op: typeof OP.SQLITE_INSERT
}

export type Condition = UpdateCondition | DeleteCondition | InsertCondition

function match(cond: Condition, op: Operation): boolean {
  if (cond.tbl != op.tbl) return false
  if (cond.op != op.op) return false
  // if INSERT no reason to check rowids
  if (cond.op == OP.SQLITE_INSERT) return true
  // if UPDATE or DELETE check rowid in rowids
  if (cond.ids === undefined) return true
  // TODO: Should convert rowids to BigInt?
  if (cond.ids.includes(Number(op.rowid))) return true
  return false
}

export class Watcher {
  updates: Array<Operation>
  emitter = new EventEmitter()

  constructor(db: unknown, capi: any) {
    this.updates = []
    capi.sqlite3_update_hook(db, this.onUpdate.bind(this), 1)
    capi.sqlite3_rollback_hook(db, this.onRollback.bind(this), 1)
    capi.sqlite3_commit_hook(db, this.onCommit.bind(this), 1)
  }

  private onUpdate(...x: RawOperation) {
    this.updates.push({ p: x[0], op: x[1], dbName: x[2], tbl: x[3], rowid: x[4] })
  }

  private onRollback() {
    this.updates.splice(0)
  }

  private onCommit() {
    this.emitter.emit('commit', [...this.updates]) // memcopy
    this.updates.splice(0)
  }

  public async watch(conds: Array<Condition>): Promise<Array<Operation>> {
    return new Promise((resolve) => {
      const handler = (ops: Array<Operation>) => {
        const matched = ops.filter((op) => conds.some((cond) => match(cond, op)))
        if (matched.length) {
          this.emitter.off('commit', handler)
          resolve(matched)
        }
      }
      this.emitter.on('commit', handler)
    })
  }
}
